import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, LineChart, Briefcase, Wallet, Bell, UserRound, ShieldCheck, Users, Landmark, CandlestickChart,
  FileCheck2, Megaphone, Layers, FileBarChart2, KeyRound, Menu, LogOut, Sun, Moon, Minus, Plus, GraduationCap, FlaskConical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { useT } from "@/i18n";
import { api } from "@/lib/api";

const CLIENT_NAV = [
  ["/painel", "nav.dashboard", LayoutDashboard], ["/mercados", "nav.markets", LineChart], ["/carteira", "nav.portfolio", Briefcase],
  ["/financeiro", "nav.wallet", Wallet], ["/notificacoes", "nav.notifications", Bell], ["/perfil", "nav.profile", UserRound],
  ["/seguranca", "nav.security", ShieldCheck],
];
const ADMIN_NAV = [
  ["/admin", "nav.admin.overview", LayoutDashboard, null], ["/admin/clientes", "nav.admin.clients", Users, "clients"],
  ["/admin/financeiro", "nav.admin.finance", Landmark, "finance"], ["/admin/operacoes", "nav.admin.trading", CandlestickChart, "trading"],
  ["/admin/documentos", "nav.admin.documents", FileCheck2, "documents"], ["/admin/comunicados", "nav.admin.notifications", Megaphone, "notifications"],
  ["/admin/ativos", "nav.admin.assets", Layers, "assets"], ["/admin/relatorios", "nav.admin.reports", FileBarChart2, "reports"],
  ["/admin/administradores", "nav.admin.admins", KeyRound, "admins"], ["/seguranca", "nav.security", ShieldCheck, null],
];

const NavList = ({ items, onNavigate }) => {
  const t = useT();
  return (
    <nav className="flex flex-col gap-1">
      {items.map(([to, key, Icon]) => (
        <NavLink key={to} to={to} end={to === "/admin"} onClick={onNavigate} data-testid={`nav-${to.replaceAll("/", "-").slice(1) || "root"}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-3 text-[15px] font-medium transition-colors duration-200 ${isActive ? "bg-primary/12 text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
          <Icon className="h-[18px] w-[18px]" />{t(key)}
        </NavLink>
      ))}
    </nav>
  );
};

const PrefsControls = () => {
  const { theme, toggleTheme, stepFont, beginner, setBeginner } = usePrefs();
  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" aria-label="Diminuir fonte" data-testid="font-decrease-btn" onClick={() => stepFont(-1)}><Minus className="h-4 w-4" /></Button>
      <span className="hidden sm:inline text-xs font-bold text-muted-foreground select-none">Aa</span>
      <Button variant="ghost" size="icon" aria-label="Aumentar fonte" data-testid="font-increase-btn" onClick={() => stepFont(1)}><Plus className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" aria-label="Modo iniciante" title="Modo iniciante (dicas)" data-testid="beginner-toggle-btn"
        className={beginner ? "text-primary" : ""} onClick={() => setBeginner(!beginner)}><GraduationCap className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" aria-label="Alternar tema" data-testid="theme-toggle-btn" onClick={toggleTheme}>
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>
    </div>
  );
};

const UnreadBell = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const seen = useRef(null);
  useEffect(() => {
    const load = () => api.get("/me/notifications").then((r) => {
      setCount(r.data.filter((n) => !n.read).length);
      const ids = new Set(r.data.map((n) => n.id));
      if (seen.current) {
        r.data.filter((n) => !seen.current.has(n.id) && n.kind === "alert").forEach((n) => toast.info(n.title, { description: n.body }));
      }
      seen.current = ids;
    }).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    window.addEventListener("tr:notifications", load);
    return () => { clearInterval(id); window.removeEventListener("tr:notifications", load); };
  }, []);
  return (
    <Button variant="ghost" size="icon" className="relative" aria-label="Notificações" data-testid="header-notifications-btn" onClick={() => navigate("/notificacoes")}>
      <Bell className="h-4 w-4" />
      {count > 0 && <span data-testid="header-unread-count" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">{count}</span>}
    </Button>
  );
};

export default function AppShell({ admin = false }) {
  const { user, logout } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = admin
    ? ADMIN_NAV.filter(([, , , p]) => !p || user.is_super || (user.permissions || []).includes(p))
    : CLIENT_NAV;
  const doLogout = async () => { await logout(); navigate("/entrar"); };

  const side = (
    <div className="flex h-full flex-col gap-8 p-5">
      <Logo size={44} />
      {admin && <div className="eyebrow -mt-4 text-primary">Painel administrativo</div>}
      <NavList items={items} onNavigate={() => setOpen(false)} />
      <div className="mt-auto rounded-xl border border-violet-400/30 bg-violet-400/10 p-3 text-xs">
        <div className="flex items-center gap-1.5 font-bold text-violet-400"><FlaskConical className="h-3.5 w-3.5" />{t("demo.badge")}</div>
        <p className="mt-1 text-muted-foreground">{t("demo.note")}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:block sticky top-0 h-screen border-r border-border bg-card/60">{side}</aside>
      <div className="min-w-0">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-2 border-b border-border bg-background/80 px-4 backdrop-blur-xl md:px-8">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Menu" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">{side}</SheetContent>
          </Sheet>
          <div className="lg:hidden"><Logo compact size={36} /></div>
          <span className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-violet-400/40 bg-violet-400/10 px-3 py-1 text-[11px] font-bold tracking-wider text-violet-400" data-testid="header-demo-badge">
            <FlaskConical className="h-3 w-3" />{t("demo.badge")}
          </span>
          <div className="ml-auto flex items-center gap-1">
            <PrefsControls />
            {!admin && <UnreadBell />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="ml-1 grid h-9 w-9 place-items-center rounded-full bg-secondary font-display text-sm font-bold text-secondary-foreground">
                  {user.name?.[0]?.toUpperCase() || "U"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs font-normal text-muted-foreground">{user.email}</div>
                  {user.client_code && <div className="num text-xs font-normal text-primary mt-1">{user.client_code}</div>}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!admin && <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/perfil")}>Perfil e documentos</DropdownMenuItem>}
                <DropdownMenuItem data-testid="menu-security" onClick={() => navigate("/seguranca")}>Segurança</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-cookies" onClick={() => window.dispatchEvent(new Event("tr:cookies"))}>Preferências de cookies</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={doLogout} className="text-down"><LogOut className="mr-2 h-4 w-4" />Sair</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="mx-auto max-w-[1500px] p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
